/** @jsx jsx */
import React, { useState, useCallback, useEffect } from 'react';
import { jsx } from '@emotion/core';
import { InputErrorText } from '../styled';
import { Checkbox, Alert, AlertVariant } from 'react-magma-dom';
import { ProfileEventTypes, useProfileUpComm } from '../screens/Profile/ProfileData';
import { InstitutionPicker } from '../screens/Profile/institution/InstitutionPicker';
import { RequiredFieldNotice } from '../basic/RequiredFieldNotice';
import { useTrackLoadUnload, trackingClient } from '../../ext/tracking';
import { EnterAwareButton } from '../magma-wrap/EnterAwareButton';
import { Card } from '../magma-wrap/Card';
import { Marker } from '../meta/Marker';
import { getInstitutionById, UserType } from '../../api';
import { Option } from '../magma-wrap/shared';
import { SupervisorNameInput } from '../data-inputs/validated/SupervisorNameInput';
import { SupervisorEmailInput } from '../data-inputs/validated/SupervisorEmailInput';
import { SupervisorPhoneNumberInput } from '../data-inputs/validated/SupervisorPhoneNumberInput';
import { InstitutionNameInput } from '../data-inputs/validated/InstitutionNameInput';
import { InstitutionCityInput } from '../data-inputs/validated/InstitutionCityInput';
import { InstitutionStateInput } from '../data-inputs/validated/InstitutionStateInput';
import { FormHeaderInfo } from '../basic/FormHeaderInfo';
import { navigateTo } from '../../navigation/path-builder';
import { setLocalStorageItem } from '../../utility';
import { UserDataKeys } from '../screens/Profile/UserDataKeys';
import { FacultyDataKeys } from '../screens/Profile/FacultyDataKeys';
import { CountrySelect } from '../screens/Profile/CountrySelect';

export const TEST_ID = {
  MARKER: 'marker-faculty-landing',
  ERR_TEST_ID: 'error-alert',
  NEW_REG: 'E9EEAD30-7595-4089-919F-500E6024D835',
  OLD_REG: 'A3E9BD13-AEA8-4B1C-BF31-D36997A8C453',
  MANUAL_VERIFICATION_LINK: 'link-manual-verification',
  MANUAL_VERIFICATION_CHECKBOX: 'checkbox-manual-verification',
  SUBMIT_TEST_ID: 'submit-button',
};

export interface FacultyLandingProps {
  username?: string;
  institutionId?: string;
}

export const FacultyLanding: React.FC<FacultyLandingProps> = (props) => {
  const [error, setError] = useState('');
  const [institutionId, setInstitutionId] = useState('');
  const [supervisorName, setSupervisorName] = useState('');
  const [supervisorEmail, setSupervisorEmail] = useState('');
  const [supervisorPhone, setSupervisorPhone] = useState('');
  const [entityName, setEntityName] = useState('');
  const [entityCity, setEntityCity] = useState('');
  const [entityState, setEntityState] = useState('');
  const [entityCountry, setEntityCountry] = useState('');
  const [entityRegion, setEntityRegion] = useState('');

  const upComm = useProfileUpComm();

  const [countrySelectionEnabled, setCountrySelectionEnabled] = useState(false);
  const [manualVerification, setManualVerification] = useState(false);

  const trackCategory = trackingClient.event.facultyRegistrationDetails;
  useTrackLoadUnload(trackCategory);

  const isSubmitDisabled = useCallback(() => {
    return !(upComm?.ctx.isValid);
  }, [upComm]);

  const onInstitutionPickerError = () => {
    upComm?.sendMsg({
      type: ProfileEventTypes.dataChanged,
      data: {
        key: UserDataKeys.instId,
        value: null
      }
    });
  };

  const navigateWithInstitutionId = () => {
    const options = { institutionId };
    if (upComm?.ctx?.overrides?.values?.[UserDataKeys.autoVerifyUser]) {
      navigateTo.facultyRegister.internationalFacultyCreateProfile(options);
    }
    else {
      getInstitutionById(institutionId).then((response) => {
        if (response && response.result && response.result.ok && response.body && !!response.body.autoVerify) {
          navigateTo.facultyRegister.facultyCreateProfile(options);
        } else {
          navigateTo.facultyRegister.internationalFacultyCreateProfile(options);
        }
      }).catch(err => {
        console.log(err);
        setError(err.message);
      });
    }
  };

  const saveInstitutionDetailAndNavigateToFacultyDiscover = async () => {
    if (!institutionId && !!manualVerification) {
      const institutionDetails: any = {};
      institutionDetails[`${FacultyDataKeys.supervisorName}`] = supervisorName;
      institutionDetails[`${FacultyDataKeys.supervisorEmail}`] = supervisorEmail;
      institutionDetails[`${FacultyDataKeys.supervisorPhone}`] = supervisorPhone;
      institutionDetails[`${FacultyDataKeys.entityName}`] = entityName;
      institutionDetails[`${FacultyDataKeys.entityCity}`] = entityCity;
      institutionDetails[`${FacultyDataKeys.entityState}`] = entityState;
      institutionDetails[`${FacultyDataKeys.entityCountry}`] = entityCountry;
      institutionDetails[`${FacultyDataKeys.entityRegion}`] = entityRegion;
      setLocalStorageItem('institution', JSON.stringify(institutionDetails));
    }
    if (institutionId && institutionId.trim().length > 0) {
      navigateWithInstitutionId();
    } else {
      navigateTo.facultyRegister.internationalFacultyCreateProfile({});
    }
  };

  useEffect(() => {
    setLocalStorageItem('institution', null);
    setManualVerification(countrySelectionEnabled);
  }, [countrySelectionEnabled]);

  return (
    <React.Fragment>
      <Marker testId={TEST_ID.MARKER}/>

      <FormHeaderInfo>
        Create Account
      </FormHeaderInfo>
      <RequiredFieldNotice/>

      {error && (
        <Alert variant={AlertVariant.warning} testId={TEST_ID.ERR_TEST_ID}>
          <InputErrorText>{error}</InputErrorText>
        </Alert>
      )}

      <React.Fragment>
        <Card style={{ padding: 3 }}>
          {/* Fragment used to keep Institution picker with the cannot find link */}
          <InstitutionPicker
            required={true}
            upComm={upComm}
            disabled={!!manualVerification}
            userType={UserType.instructor}
            onChange={(option: Option) => {
              if (option.label && option.item) {
                setManualVerification(false);
                setInstitutionId(option.item);
              }

            }}
            onError={onInstitutionPickerError}
          />
          <Checkbox
            testId={TEST_ID.MANUAL_VERIFICATION_CHECKBOX}
            checked={manualVerification}
            onChange={({ target: { checked } }) => {
              setCountrySelectionEnabled(checked);
              trackCategory.manuallyValidateClick();
            }}
            labelText="I could not find my institution"
          />
          {manualVerification && (
            <div>
              <p>
                Provide more information to verify your identity as an instructor. You will not have access to Cengage
                learning materials until we can verify your identity.
              </p>
              <SupervisorNameInput
                upComm={upComm}
                required={true}
                onBlur={e => setSupervisorName(e.target.value)}
              />
              <SupervisorEmailInput
                upComm={upComm}
                required={true}
                onBlur={e => setSupervisorEmail(e.target.value)}
              />
              <SupervisorPhoneNumberInput
                upComm={upComm}
                required={true}
                onBlur={e => setSupervisorPhone(e.target.value)}
              />
              <InstitutionNameInput required={true}
                upComm={upComm}
                onBlur={e => setEntityName(e.target.value)}
              />
              <InstitutionCityInput
                upComm={upComm}
                required={true}
                onBlur={e => setEntityCity(e.target.value)}
              />
              <InstitutionStateInput
                upComm={upComm}
                required={true}
                onBlur={e => setEntityState(e.target.value)}
              />
              <CountrySelect
                upComm={upComm}
                required={true}
                disabled={!countrySelectionEnabled}
                onChange={(option: Option) => {
                  if (option.label && option.item && option.region) {
                    setEntityCountry(option.item);
                    setEntityRegion(option.region);
                  }
                }}
              />
            </div>
          )}
        </Card>
      </React.Fragment>

      <EnterAwareButton
        style={{ minWidth: '230px' }}
        isFullWidth={true}
        value="Next"
        disabled={isSubmitDisabled()}
        onClick={() => saveInstitutionDetailAndNavigateToFacultyDiscover()}
        testId={TEST_ID.SUBMIT_TEST_ID}
        id="submit-button"
      />
    </React.Fragment>
  );
};